/**
 * KunyeDuzenleModal.jsx — Kişi Detay başlığındaki "Düzenle ve Gör" penceresi (backlog #367, backend #366).
 *
 * İki bölüm, İKİ AYRI KAYDET:
 *   1. Künye (kimlik no, ad, soyad, uyruk, telefon, e-posta, pasaport no + ülke) → PATCH /personnel/{id}/kunye.
 *      Yalnızca YÜKLENEN künyeye göre DEĞİŞEN anahtarlar gider (kırpılmış); temizlenebilen alan (telefon, e-posta,
 *      pasaport no / ülke) boşaltılınca `null` gider; ilk dört kimlik alanı boşaltılamaz (burada da engellenir). ⚠️ Eski
 *      PUT /kartduzenle KULLANILMAZ: gönderilmeyen alanı siler.
 *      409 KIMLIK_NO_KULLANIMDA → "başka kartta kayıtlı" + o karta bağlantı (birleştirme #289); 409 BENZER_KAYIT → adaylar
 *      + "Farklı kişi, yine de kaydet" (`benzerKayitOnaylandi: true` ile aynı gövde); 400 → `hatalar` alanın altında.
 *      Yeni T.C. / uyruk kimlik doğrulamasını sıfırlar: kaydetmeden önce küçük bir uyarı, sonra başarı mesajında söylenir.
 *   2. Ek Bilgiler (personel bilgi kataloğu; eski BilgilerKarti'nin taşınmış hali) → PUT /personnel/{id}/bilgi. Hepsi ya
 *      da hiçbiri kaydedilir; yalnızca DEĞİŞEN kodlar gönderilir (emekli bir kategorinin eski değeri yüzünden toplu reddedilmesin).
 *      `fuarBazli` kategoriler üst çubuktaki fuarı kullanır; fuar yokken devre dışıdır.
 *
 * Kendi kapanışını yönetir: künye kaydı başarılıysa `onSaved(mesaj)` sonra `onClose()`; Ek Bilgiler kaydı pencereyi
 * AÇIK bırakır (yalnızca `onSaved(mesaj)` — üst sayfa sessizce yenilemeli, pencereyi sökmemeli).
 *
 * @param {number}   personId
 * @param {number}   fuarId    üst çubuk fuarı (yoksa null)
 * @param {object}   kunye     GET /personnel/{id}/kunye
 * @param {Array}    bilgiler  kişi satırındaki `bilgiler[]` (dizi değilse pencere kendisi okur)
 * @param {Function} onClose
 * @param {Function} onSaved   `(mesaj)`
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Alert, Badge, Button, Col, Collapse, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader,
  Row, Spinner, Table,
} from "reactstrap";
import { ChevronDown, ChevronRight, History, ListChecks, Save } from "lucide-react";
import TarihGirdisi from "../../Common/TarihGirdisi";
import TelefonGirdisi from "../../Common/TelefonGirdisi";
import { getUlkeListesi, patchKunye } from "../../../services/kisiKunyeService";
import { getPersonBilgi, isStaffRefusal, KIMLIK_DURUM, savePersonBilgi } from "../../../services/kisiAdminService";
import { kimlikUyarisi } from "../kimlikNo";
import { currentLocaleTag } from "../../../i18n";

const ALAN_ETIKET = {
  kimlikNo: "T.C./Pasaport No",
  ad: "Ad",
  soyad: "Soyad",
  uyrukId: "Uyruk",
  telefon: "Telefon",
  eposta: "E-posta",
  pasaportNo: "Pasaport No",
  pasaportUlkeId: "Pasaport Ülkesi",
  kimlikDogrulamaDurum: "Kimlik doğrulama",
};

const ALANLAR = ["kimlikNo", "ad", "soyad", "uyrukId", "telefon", "eposta", "pasaportNo", "pasaportUlkeId"];
const KIMLIK_ALANLARI = ["kimlikNo", "ad", "soyad", "uyrukId"]; // temizlenemez
const UYRUK = { 1: "T.C.", 2: "Yabancı" };

// PersonelEkleModal'daki benzer-kişi uyarısıyla aynı anahtarlar (`adaylar[].fark`, backend #287).
const BENZER_FARK = {
  HANE_DEGISIK: "bir hane farklı",
  YER_DEGISMIS: "iki hane yer değiştirmiş",
  EKSIK_HANE: "yazdığınız numarada bir hane eksik",
  FAZLA_HANE: "yazdığınız numarada bir hane fazla",
};

function formdanKunye(k) {
  const s = (v) => (v == null ? "" : String(v));
  return {
    kimlikNo: s(k.kimlikNo),
    ad: s(k.ad),
    soyad: s(k.soyad),
    uyrukId: k.uyrukId === 1 || k.uyrukId === 2 ? String(k.uyrukId) : "", // eski kartlarda 0 → seçilmemiş
    telefon: s(k.telefon),
    eposta: s(k.eposta),
    pasaportNo: s(k.pasaportNo),
    pasaportUlkeId: s(k.pasaportUlkeId),
  };
}

/** Yüklenen künyeye göre değişen alanların PATCH gövdesi. */
function degisenGovde(ilk, form) {
  const govde = {};
  ALANLAR.forEach((alan) => {
    const yeni = String(form[alan]).trim();
    if (yeni === String(ilk[alan]).trim()) return;
    if (alan === "uyrukId") {
      if (yeni !== "") govde.uyrukId = Number(yeni);
    } else if (KIMLIK_ALANLARI.includes(alan)) {
      if (yeni !== "") govde[alan] = yeni;
    } else if (yeni === "") {
      govde[alan] = null;
    } else {
      govde[alan] = alan === "pasaportUlkeId" ? Number(yeni) : yeni;
    }
  });
  return govde;
}

/** Kimlik alanı boşaltıldıysa (ilk değer doluyken) alan başına istemci hatası. */
function bosHatalar(ilk, form) {
  const hatalar = {};
  KIMLIK_ALANLARI.forEach((alan) => {
    if (String(form[alan]).trim() === "" && String(ilk[alan]).trim() !== "") hatalar[alan] = "Boş bırakılamaz.";
  });
  return hatalar;
}

function tarihYaz(v) {
  if (!v) return "—";
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

function gecmisDeger(alan, v, ulkeAdlari) {
  if (v == null || v === "") return null;
  if (alan === "uyrukId") return UYRUK[v] ?? String(v);
  if (alan === "kimlikDogrulamaDurum") return KIMLIK_DURUM[v]?.label ?? String(v);
  if (alan === "pasaportUlkeId") return ulkeAdlari[String(v)] ?? `#${v}`;
  return String(v);
}

function hataMetni(e) {
  if (isStaffRefusal(e)) return "Bu işlem yalnızca SAKA personeli içindir.";
  if (e?.status == null) return "Sunucu yanıt vermedi.";
  return e?.message || "İşlem başarısız oldu.";
}

/** Başka bir kişi kartına YENİ SEKMEDE açılan adres; kaynak sayfa `geri` ile gider (#262 geri bağlantısı). */
function kartAdresi(personId, geri) {
  const p = new URLSearchParams({ geri, geriEtiket: "Önceki Karta Dön" });
  return `/Management/Kisi/Personeller/${personId}?${p.toString()}`;
}

function MetinAlani({ id, etiket, deger, onDegis, hata, uyari, maxLength, type = "text", disabled }) {
  return (
    <>
      <Label for={id} className="small mb-1">{etiket}</Label>
      <Input
        id={id} bsSize="sm" type={type} value={deger} maxLength={maxLength} disabled={disabled}
        invalid={Boolean(hata)} onChange={(e) => onDegis(e.target.value)}
      />
      {hata && <FormFeedback>{hata}</FormFeedback>}
      {!hata && uyari && <div className="form-text text-warning mt-1">{uyari}</div>}
    </>
  );
}

/** Ek Bilgiler girdisi: `veriTipi`ne göre SECIM / EVET_HAYIR / TARIH / SAYI / METIN. */
function BilgiAlani({ b, deger, onDegis, fuarYok, kilitli }) {
  const disabled = kilitli || fuarYok || b.aktif === false;
  const ortak = { bsSize: "sm", value: deger, disabled, onChange: (e) => onDegis(e.target.value) };
  return (
    <>
      <Label className="small mb-1">
        {b.ad}
        {b.zorunlu && <span className="text-warning ms-1">*</span>}
        {b.aktif === false && <Badge color="secondary" className="ms-2 fw-normal">Emekli</Badge>}
        {b.firmaGirebilir === false && <Badge color="primary" className="ms-2 fw-normal">SAKA</Badge>}
      </Label>
      {b.veriTipi === "SECIM" ? (
        <Input type="select" {...ortak}>
          <option value="">—</option>
          {(b.secenekler || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </Input>
      ) : b.veriTipi === "EVET_HAYIR" ? (
        <Input type="select" {...ortak}>
          <option value="">—</option>
          <option value="1">Evet</option>
          <option value="0">Hayır</option>
        </Input>
      ) : b.veriTipi === "TARIH" ? (
        // ⚠️ TARIH yalnızca ISO (yyyy-AA-gg) kabul eder; TarihGirdisi gg/aa/yyyy gösterir ama tam onu verir (#560).
        <TarihGirdisi value={deger} disabled={disabled} onChange={onDegis} />
      ) : (
        <Input
          {...ortak}
          type={b.veriTipi === "SAYI" ? "number" : "text"}
          maxLength={b.veriTipi === "METIN" ? 1000 : undefined}
        />
      )}
      {fuarYok
        ? <div className="form-text mt-1">Önce üst çubuktan bir fuar seçin</div>
        : b.aciklama && <div className="form-text small mt-1">{b.aciklama}</div>}
    </>
  );
}

export default function KunyeDuzenleModal({ personId, fuarId, kunye, bilgiler, onClose, onSaved }) {
  const konum = useLocation();
  const geri = `${konum.pathname}${konum.search}`;

  // ── Künye ──────────────────────────────────────────────────────────────────────
  const ilk = useMemo(() => formdanKunye(kunye), [kunye]);
  const [form, setForm] = useState(() => formdanKunye(kunye));
  const [kaydediyor, setKaydediyor] = useState(false);
  const [sunucuHatalari, setSunucuHatalari] = useState({});
  const [genelHata, setGenelHata] = useState(null);
  const [kimlikCakisma, setKimlikCakisma] = useState(null); // { personId, kartlar[] }
  const [benzer, setBenzer] = useState(null); // { adaylar[] }
  const [birlesti, setBirlesti] = useState(null); // { kalanPersonId }
  const [ulkeler, setUlkeler] = useState([]);
  const [ulkeHatasi, setUlkeHatasi] = useState(false); // liste okunamazsa ülke kutusu sayısal kimliğe düşer
  const [gecmisAcik, setGecmisAcik] = useState(false);

  useEffect(() => {
    let iptal = false;
    async function oku() {
      try {
        const liste = await getUlkeListesi();
        if (!iptal) setUlkeler(liste);
      } catch {
        if (!iptal) setUlkeHatasi(true);
      }
    }
    oku();
    return () => { iptal = true; };
  }, []);

  const ulkeAdlari = useMemo(
    () => Object.fromEntries(ulkeler.map((u) => [String(u.countryId), u.countryName])),
    [ulkeler],
  );

  const govde = degisenGovde(ilk, form);
  const bosHata = bosHatalar(ilk, form);
  const degisti = Object.keys(govde).length > 0;
  const hata = (alan) => bosHata[alan] || sunucuHatalari[alan] || null;
  // #702: a T.C. person is not asked for a passport number and country. Nothing stored (or typed) is hidden: the two boxes stay while the card
  // has a passport, while a value of theirs is in the PATCH body, and whenever the uyruk is anything but T.C. (WAS: always drawn).
  const pasaportAlanlari = form.uyrukId !== "1" || ilk.pasaportNo !== "" || ilk.pasaportUlkeId !== ""
    || "pasaportNo" in govde || "pasaportUlkeId" in govde;

  const kimlikNoDegisti = String(form.kimlikNo).trim() !== ilk.kimlikNo.trim();
  const kimlikDogrulama = kunye.kimlikDogrulama;
  const sifirlanir = (kimlikDogrulama?.durum === 1 || kimlikDogrulama?.durum === 2)
    && (kimlikNoDegisti || "uyrukId" in govde
      || (kimlikDogrulama?.belgeTuru === "PASAPORT" && "pasaportNo" in govde));

  function alanDegis(alan, deger) {
    setForm((f) => ({ ...f, [alan]: deger }));
    setSunucuHatalari((h) => {
      if (!h[alan]) return h;
      const kalan = { ...h };
      delete kalan[alan];
      return kalan;
    });
    setGenelHata(null);
    setKimlikCakisma(null);
    setBenzer(null);
    setBirlesti(null);
  }

  // `benzerOnay` yalnızca BENZER_KAYIT uyarısındaki düğmeden true gelir (onClick'ten olay değil).
  async function kaydet(benzerOnay = false) {
    setKaydediyor(true);
    setSunucuHatalari({});
    setGenelHata(null);
    setKimlikCakisma(null);
    setBenzer(null);
    setBirlesti(null);
    try {
      const out = await patchKunye(personId, benzerOnay === true ? { ...govde, benzerKayitOnaylandi: true } : govde);
      // "Değişiklik yok." — hiçbir şey yazılmadı: başarı iddia etmeden kapat.
      if (Array.isArray(out.degisenAlanlar) && out.degisenAlanlar.length === 0) {
        onClose();
        return;
      }
      onSaved?.(`Künye güncellendi.${out.kimlikDogrulamaSifirlandi ? " Kimlik doğrulaması sıfırlandı." : ""}`);
      onClose();
    } catch (e) {
      if (e.status === 409 && e.kod === "KIMLIK_NO_KULLANIMDA") {
        setKimlikCakisma({ personId: e.personId, kartlar: e.kartlar });
      } else if (e.status === 409 && e.kod === "BENZER_KAYIT") {
        setBenzer({ adaylar: e.adaylar });
      } else if (e.status === 409 && e.kod === "BIRLESTIRILMIS_KART") {
        setBirlesti({ kalanPersonId: e.kalanPersonId });
      } else if (e.status === 400 && e.hatalar && Object.keys(e.hatalar).length > 0) {
        setSunucuHatalari(e.hatalar);
        // Forma bağlanamayan anahtarlar (alan adı dışı) genel hata olarak da görünsün.
        const artan = Object.entries(e.hatalar).filter(([k]) => !ALANLAR.includes(k));
        if (artan.length > 0) setGenelHata(artan.map(([k, v]) => `${k}: ${v}`).join(" · "));
      } else {
        setGenelHata(hataMetni(e));
      }
    } finally {
      setKaydediyor(false);
    }
  }

  // ── Ek Bilgiler ────────────────────────────────────────────────────────────────
  const [liste, setListe] = useState(() => (Array.isArray(bilgiler) ? bilgiler : undefined)); // undefined: okunacak
  const [duzenlemeler, setDuzenlemeler] = useState({}); // kod → yazılan değer (yalnızca dokunulanlar)
  const [bilgiKaydediyor, setBilgiKaydediyor] = useState(false);
  const [bilgiHata, setBilgiHata] = useState(null);
  const [bilgiMesaj, setBilgiMesaj] = useState(null);

  useEffect(() => {
    if (liste !== undefined) return undefined;
    let iptal = false;
    async function oku() {
      try {
        const d = await getPersonBilgi(personId, fuarId);
        if (!iptal) setListe(Array.isArray(d) ? d : []);
      } catch (e) {
        if (!iptal) {
          setListe([]);
          setBilgiHata(hataMetni(e));
        }
      }
    }
    oku();
    return () => { iptal = true; };
  }, [liste, personId, fuarId]);

  const degisenKodlar = (liste || [])
    .filter((b) => b.kod in duzenlemeler && String(duzenlemeler[b.kod]) !== String(b.deger ?? ""))
    .map((b) => b.kod);

  function bilgiDegis(kod, deger) {
    setDuzenlemeler((d) => ({ ...d, [kod]: deger }));
    setBilgiMesaj(null);
    setBilgiHata(null);
  }

  async function bilgiKaydet() {
    setBilgiKaydediyor(true);
    setBilgiHata(null);
    setBilgiMesaj(null);
    try {
      const degerler = Object.fromEntries(degisenKodlar.map((kod) => [kod, duzenlemeler[kod]]));
      const sonraki = await savePersonBilgi(personId, { fuarId: fuarId || 0, degerler });
      if (Array.isArray(sonraki)) setListe(sonraki);
      setDuzenlemeler({});
      setBilgiMesaj("Ek bilgiler kaydedildi.");
      onSaved?.("Ek bilgiler kaydedildi.");
    } catch (e) {
      setBilgiHata(hataMetni(e));
    } finally {
      setBilgiKaydediyor(false);
    }
  }

  const mesgul = kaydediyor || bilgiKaydediyor;
  const adSoyad = [kunye.ad, kunye.soyad].filter(Boolean).join(" ");
  // #698: the HES code is no longer shown or edited here; the backend still returns its old history rows, which would
  // print the raw field name, so they are left out (the rows stay in the database).
  const gecmis = Array.isArray(kunye.gecmis) ? kunye.gecmis.filter((g) => g.alan !== "hesKodu") : [];
  const ulkeSecimde = form.pasaportUlkeId !== "" && !ulkeAdlari[form.pasaportUlkeId];

  return (
    <Modal isOpen size="lg" scrollable toggle={() => !mesgul && onClose()}>
      <ModalHeader toggle={() => !mesgul && onClose()}>
        Düzenle ve Gör{adSoyad && <span className="text-muted fw-normal fs-6"> · {adSoyad}</span>}
      </ModalHeader>
      <ModalBody>
        {genelHata && <Alert fade={false} color="danger" className="small py-2">{genelHata}</Alert>}

        {birlesti && (
          <Alert fade={false} color="danger" className="small py-2">
            Bu kart başka bir karta birleştirilmiş.
            {birlesti.kalanPersonId && (
              <> <Link className="alert-link" to={kartAdresi(birlesti.kalanPersonId, geri)} target="_blank" rel="noopener noreferrer">Güncel kartı aç</Link></>
            )}
          </Alert>
        )}

        {kimlikCakisma && (
          <Alert fade={false} color="danger" className="small py-2">
            <div className="fw-semibold">Bu numara başka bir kartta kayıtlı</div>
            {(kimlikCakisma.kartlar.length > 0 ? kimlikCakisma.kartlar : [{ personId: kimlikCakisma.personId }])
              .filter((k) => k.personId != null)
              .map((k) => (
                <div key={k.personId}>
                  <Link className="alert-link" to={kartAdresi(k.personId, geri)} target="_blank" rel="noopener noreferrer">
                    {[k.ad, k.soyad].filter(Boolean).join(" ") || "Kartı aç"} #{k.personId}
                  </Link>
                  {k.kimlikDogrulamaDurumAdi && <span className="text-muted"> · Kimlik: {k.kimlikDogrulamaDurumAdi}</span>}
                </div>
              ))}
          </Alert>
        )}

        {benzer && (
          <Alert fade={false} color="warning" className="small py-2">
            <div className="fw-semibold mb-1">Bu kişi zaten kayıtlı olabilir mi?</div>
            <div className="mb-2">
              Aynı ad soyadla, kimlik numarası tek hane farklı {benzer.adaylar.length > 1 ? "kayıtlar" : "bir kayıt"} var.
            </div>
            {benzer.adaylar.map((a) => (
              <div key={a.personId ?? a.kimlikNoMasked} className="p-2 rounded border bg-body mb-2">
                <div className="fw-semibold">
                  {a.ad} {a.soyad}
                  {a.yasakli && <Badge color="danger" className="ms-2 fw-normal">Girişi yasaklı</Badge>}
                  {a.personId != null && (
                    <Link className="ms-2 fw-normal" to={kartAdresi(a.personId, geri)} target="_blank" rel="noopener noreferrer">
                      Kartı aç #{a.personId}
                    </Link>
                  )}
                </div>
                <div className="text-muted">
                  {a.kimlikNo || a.kimlikNoMasked}{BENZER_FARK[a.fark] ? ` — ${BENZER_FARK[a.fark]}` : ""}
                </div>
                <div className="text-muted">Kimlik: {a.kimlikDogrulamaDurumAdi || "Doğrulanmadı"}</div>
                {a.sonFirmaAdi && (
                  <div className="text-muted">Son görev: {[a.sonFirmaAdi, a.sonFuarAdi].filter(Boolean).join(" · ")}</div>
                )}
              </div>
            ))}
            <div className="d-flex gap-2">
              <Button color="warning" size="sm" disabled={kaydediyor} onClick={() => kaydet(true)}>
                {kaydediyor ? <Spinner size="sm" /> : "Farklı kişi, yine de kaydet"}
              </Button>
              <Button color="light" size="sm" disabled={kaydediyor} onClick={() => setBenzer(null)}>Vazgeç</Button>
            </div>
          </Alert>
        )}

        {/* ── 1 · Künye ── */}
        <h6 className="text-uppercase text-muted fs-12 mb-2">Künye</h6>
        <Row className="g-2">
          <Col md={6}>
            <MetinAlani
              id="kd-kimlikNo" etiket={ALAN_ETIKET.kimlikNo} deger={form.kimlikNo} maxLength={50} disabled={kaydediyor}
              onDegis={(v) => alanDegis("kimlikNo", v)} hata={hata("kimlikNo")}
              uyari={kimlikNoDegisti ? kimlikUyarisi(form.kimlikNo, form.uyrukId) : null}
            />
          </Col>
          <Col md={6}>
            <Label for="kd-uyrukId" className="small mb-1">{ALAN_ETIKET.uyrukId}</Label>
            <Input
              id="kd-uyrukId" type="select" bsSize="sm" value={form.uyrukId} disabled={kaydediyor}
              invalid={Boolean(hata("uyrukId"))} onChange={(e) => alanDegis("uyrukId", e.target.value)}
            >
              {ilk.uyrukId === "" && <option value="">—</option>}
              <option value="1">{UYRUK[1]}</option>
              <option value="2">{UYRUK[2]}</option>
            </Input>
            {hata("uyrukId") && <FormFeedback>{hata("uyrukId")}</FormFeedback>}
          </Col>
          {sifirlanir && (
            <Col xs={12}>
              <div className="small text-warning">Kaydedince kimlik doğrulaması sıfırlanır.</div>
            </Col>
          )}
          <Col md={6}>
            <MetinAlani
              id="kd-ad" etiket={ALAN_ETIKET.ad} deger={form.ad} maxLength={255} disabled={kaydediyor}
              onDegis={(v) => alanDegis("ad", v)} hata={hata("ad")}
            />
          </Col>
          <Col md={6}>
            <MetinAlani
              id="kd-soyad" etiket={ALAN_ETIKET.soyad} deger={form.soyad} maxLength={255} disabled={kaydediyor}
              onDegis={(v) => alanDegis("soyad", v)} hata={hata("soyad")}
            />
          </Col>
          <Col md={6}>
            {/* #746: kod seçici + ayrı numara kutusu; kayıtlı eski numara olduğu gibi gösterilir ve dokunulmazsa aynen gider (bkz. TelefonGirdisi). */}
            <Label for="kd-telefon" className="small mb-1">{ALAN_ETIKET.telefon}</Label>
            <TelefonGirdisi id="kd-telefon" bsSize="sm" value={form.telefon} disabled={kaydediyor} invalid={Boolean(hata("telefon"))} onChange={(v) => alanDegis("telefon", v)} />
            {hata("telefon") && <div className="text-danger small mt-1">{hata("telefon")}</div>}
          </Col>
          <Col md={6}>
            <MetinAlani
              id="kd-eposta" etiket={ALAN_ETIKET.eposta} type="email" deger={form.eposta} maxLength={255} disabled={kaydediyor}
              onDegis={(v) => alanDegis("eposta", v)} hata={hata("eposta")}
            />
          </Col>
          {pasaportAlanlari && (<>
          <Col md={6}>
            <MetinAlani
              id="kd-pasaportNo" etiket={ALAN_ETIKET.pasaportNo} deger={form.pasaportNo} maxLength={50} disabled={kaydediyor}
              onDegis={(v) => alanDegis("pasaportNo", v)} hata={hata("pasaportNo")}
            />
          </Col>
          <Col md={6}>
            <Label for="kd-pasaportUlkeId" className="small mb-1">{ALAN_ETIKET.pasaportUlkeId}</Label>
            {!ulkeHatasi ? (
              <Input
                id="kd-pasaportUlkeId" type="select" bsSize="sm" value={form.pasaportUlkeId} disabled={kaydediyor}
                invalid={Boolean(hata("pasaportUlkeId"))} onChange={(e) => alanDegis("pasaportUlkeId", e.target.value)}
              >
                <option value="">—</option>
                {ulkeSecimde && <option value={form.pasaportUlkeId}>#{form.pasaportUlkeId}</option>}
                {ulkeler.map((u) => <option key={u.countryId} value={String(u.countryId)}>{u.countryName}</option>)}
              </Input>
            ) : (
              // Liste okunamadıysa mevcut ülke kimliği düz kutuda kalır (KimlikPasaportModal ile aynı geri düşüş).
              <Input
                id="kd-pasaportUlkeId" bsSize="sm" value={form.pasaportUlkeId} disabled={kaydediyor}
                invalid={Boolean(hata("pasaportUlkeId"))}
                onChange={(e) => alanDegis("pasaportUlkeId", e.target.value.replace(/\D/g, ""))}
              />
            )}
            {hata("pasaportUlkeId") && <FormFeedback>{hata("pasaportUlkeId")}</FormFeedback>}
          </Col>
          </>)}
        </Row>

        {gecmis.length > 0 && (
          <div className="mt-3">
            <Button
              color="link" size="sm" className="p-0 text-decoration-none d-inline-flex align-items-center gap-1"
              aria-expanded={gecmisAcik} onClick={() => setGecmisAcik((a) => !a)}
            >
              {gecmisAcik ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <History size={14} />Değişiklik geçmişi
              <Badge color="light" className="text-body border fw-normal">{gecmis.length}</Badge>
            </Button>
            <Collapse isOpen={gecmisAcik}>
              <Table size="sm" borderless responsive className="small mt-2 mb-0 align-middle">
                <thead className="text-muted">
                  <tr><th>Tarih</th><th>Alan</th><th>Değişiklik</th><th>Değiştiren</th></tr>
                </thead>
                <tbody>
                  {gecmis.map((g) => {
                    const eski = gecmisDeger(g.alan, g.eskiDeger, ulkeAdlari);
                    const yeni = gecmisDeger(g.alan, g.yeniDeger, ulkeAdlari);
                    return (
                      <tr key={g.id ?? `${g.alan}-${g.tarih}`}>
                        <td className="text-nowrap">{tarihYaz(g.tarih)}</td>
                        <td className="text-nowrap">{ALAN_ETIKET[g.alan] ?? g.alan}</td>
                        <td className="text-break">
                          {eski ?? <span className="text-muted">—</span>}
                          <span className="text-muted mx-1">→</span>
                          {yeni ?? <span className="text-muted">—</span>}
                        </td>
                        <td>{g.degistirenAdi || <span className="text-muted">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Collapse>
          </div>
        )}

        {/* ── 2 · Ek Bilgiler (kendi Kaydet'i; künye PATCH'inden bağımsız) ── */}
        <div className="border-top mt-3 pt-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <ListChecks size={14} className="text-muted" />
            <h6 className="text-uppercase text-muted fs-12 mb-0">Ek Bilgiler</h6>
            {liste && liste.length > 0 && (
              <Button
                color="primary" outline size="sm" className="ms-auto"
                disabled={mesgul || degisenKodlar.length === 0} onClick={bilgiKaydet}
              >
                {bilgiKaydediyor ? <Spinner size="sm" /> : <><Save size={14} className="me-1" />Kaydet</>}
              </Button>
            )}
          </div>
          {bilgiHata && <Alert fade={false} color="danger" className="small py-2" toggle={() => setBilgiHata(null)}>{bilgiHata}</Alert>}
          {bilgiMesaj && <div className="small text-success mb-2">{bilgiMesaj}</div>}
          {liste === undefined ? (
            <Spinner size="sm" />
          ) : liste.length === 0 ? (
            <div className="text-muted small">
              Tanımlı ek kişi alanı yok.{" "}
              <Link to="/Management/Kisi/Kurallar?tab=kunye" target="_blank" rel="noopener noreferrer">Kişi Künye Bilgileri</Link>
            </div>
          ) : (
            <Row className="g-2">
              {liste.map((b) => (
                <Col md={6} key={b.kod}>
                  <BilgiAlani
                    b={b}
                    deger={b.kod in duzenlemeler ? duzenlemeler[b.kod] : (b.deger ?? "")}
                    onDegis={(v) => bilgiDegis(b.kod, v)}
                    fuarYok={Boolean(b.fuarBazli) && !fuarId}
                    kilitli={bilgiKaydediyor}
                  />
                </Col>
              ))}
            </Row>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" size="sm" disabled={mesgul} onClick={onClose}>Kapat</Button>
        <Button
          color="primary" size="sm"
          disabled={mesgul || !degisti || Object.keys(bosHata).length > 0 || Boolean(benzer)}
          onClick={() => kaydet()}
        >
          {kaydediyor ? <Spinner size="sm" /> : "Kaydet"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
